import type { HelpArticleCategoryId } from '../constants/helpArticles.js';

// ─── Enums ────────────────────────────────────────────────────────────────────

export enum UserRole {
  TEAM_OWNER = 'TEAM_OWNER',
  COACH = 'COACH',
  MECHANIC = 'MECHANIC',
  DRIVER = 'DRIVER',
  PARENT = 'PARENT',
}

export enum TeamInvitationStatus {
  PENDING = 'PENDING',
  ACCEPTED = 'ACCEPTED',
  CANCELLED = 'CANCELLED',
}

export enum TeamInvitationSource {
  INVITE = 'INVITE',
  REQUEST = 'REQUEST',
}

export enum KartStatus {
  ACTIVE = 'ACTIVE',
  MAINTENANCE = 'MAINTENANCE',
  RETIRED = 'RETIRED',
  AVAILABLE = 'AVAILABLE',
}

export enum SessionType {
  PRACTICE = 'PRACTICE',
  QUALIFYING = 'QUALIFYING',
  HEAT = 'HEAT',
  FEATURE = 'FEATURE',
  TEST = 'TEST',
}

export enum MaintenanceCategory {
  ENGINE = 'ENGINE',
  OIL = 'OIL',
  SPARK = 'SPARK',
  FUEL = 'FUEL',
  COOLING = 'COOLING',
  CARBURETOR = 'CARBURETOR',
  CLUTCH = 'CLUTCH',
  CHAIN = 'CHAIN',
  GEARS = 'GEARS',
  TIRES = 'TIRES',
  BRAKES = 'BRAKES',
  STEERING = 'STEERING',
  BEARINGS = 'BEARINGS',
  CHASSIS = 'CHASSIS',
  BODYWORK = 'BODYWORK',
  ELECTRICAL = 'ELECTRICAL',
  GENERAL = 'GENERAL',
}

export enum MaintenanceStatus {
  SCHEDULED = 'SCHEDULED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
}

export enum EventType {
  RACE = 'RACE',
  PRACTICE = 'PRACTICE',
  TEST_DAY = 'TEST_DAY',
  CHAMPIONSHIP = 'CHAMPIONSHIP',
  CLUB = 'CLUB',
  NATIONAL = 'NATIONAL',
}

export enum AchievementType {
  FIRST_LAP = 'FIRST_LAP',
  PERSONAL_BEST = 'PERSONAL_BEST',
  PODIUM = 'PODIUM',
  WIN = 'WIN',
  CONSISTENCY = 'CONSISTENCY',
  IMPROVEMENT = 'IMPROVEMENT',
  ATTENDANCE = 'ATTENDANCE',
  SEASON_COMPLETE = 'SEASON_COMPLETE',
}

export enum DrillCategory {
  BRAKING = 'BRAKING',
  CORNER_ENTRY = 'CORNER_ENTRY',
  APEX = 'APEX',
  CORNER_EXIT = 'CORNER_EXIT',
  OVERTAKING = 'OVERTAKING',
  RACECRAFT = 'RACECRAFT',
  FITNESS = 'FITNESS',
  MENTAL = 'MENTAL',
}

export enum DriverStatus {
  ACTIVE = 'ACTIVE',
  INJURED = 'INJURED',
  INACTIVE = 'INACTIVE',
  GRADUATED = 'GRADUATED',
}

export enum AccountSetupMode {
  PASSWORD = 'PASSWORD',
  REGISTRATION_LINK = 'REGISTRATION_LINK',
}

export enum DriverCategory {
  UNKNOWN = 'Unspecified',
  KID_KART = 'Kid Kart',
  CADET = 'Cadet',
  MICRO = 'Micro',
  MINI = 'Mini',
  JUNIOR = 'Junior',
  SENIOR = 'Senior',
  SENIOR_HEAVY = 'Senior - Heavy',
}

export enum DataFileSource {
  MYCHRON = 'MYCHRON',
  ALFANO = 'ALFANO',
  OTHER = 'OTHER',
}

export type SpeedUnit = 'KPH' | 'MPH';
export type TemperatureUnit = 'C' | 'F';
export type DistanceUnit = 'KM' | 'MI';

export interface MeasurementPreferences {
  speedUnit: SpeedUnit;
  temperatureUnit: TemperatureUnit;
  distanceUnit: DistanceUnit;
}

// ─── Core Entities ────────────────────────────────────────────────────────────

export interface User {
  id: string;
  email: string;
  /** Present for driver accounts created with a child username login. */
  username?: string | null;
  /** Stable Apple Sign in subject, when the account is linked to Apple. */
  appleSubject?: string | null;
  name: string;
  role: UserRole;
  additionalRoles?: UserRole[];
  teamId: string | null;
  avatarUrl: string | null;
  measurementPreferences?: MeasurementPreferences | null;
  /** Support-managed overrides layered on top of workspace plan entitlements. */
  featureOverrides?: UserFeatureOverrides;
  /** Omitted or true for legacy accounts; false until the user verifies self-registration. */
  emailVerified?: boolean;
  /** When true, login and API access are blocked (e.g. staff paused on Essentials). */
  accountDisabled?: boolean;
  /** When true, this internal support user may use customer masquerade support tooling. */
  supportMasqueradeAllowed?: boolean;
  /** Stripe Customer id for this user (Essentials / individual billing). */
  stripeCustomerId?: string | null;
  /** Family workspace that bills this parent separately from a club team roster. */
  familyBillingTeamId?: string | null;
  /** Last successful sign-in time. Legacy accounts may not have this until they log in again. */
  lastLoginAt?: string | null;
  /** Last authenticated API activity. Used to tell if a long-lived session is still active. */
  lastActivityAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export type UsageBillingOwnerKind = 'user' | 'team';
export type UsageBillingStatus = 'free' | 'active' | 'past_due' | 'canceled' | 'comp';

export interface UsageBillingSnapshot {
  billingMode?: 'usage' | 'fixed_monthly';
  fixedMonthlyAmountCents?: number | null;
  fixedInvoiceDaysUntilDue?: number | null;
  collectionMethod?: 'send_invoice' | 'charge_automatically';
  scheduledUsageStartsAt?: string | null;
  fixedAccessReason?: string | null;
  billingAccountId: string;
  ownerKind: UsageBillingOwnerKind;
  ownerId: string;
  ownerLabel: string;
  canManageBilling: boolean;
  partnerAccess: boolean;
  partnerProgramLabel: string | null;
  enforcementEnabled: boolean;
  promptsEnabled: boolean;
  status: UsageBillingStatus;
  stripeAvailable: boolean;
  /** Private financial detail; null for non-billing members. */
  hasPaymentMethod: boolean | null;
  /** True while Stripe still has a non-canceled subscription, including past-due subscriptions. */
  hasStripeSubscription: boolean;
  hasActiveSubscription: boolean;
  /** Private financial detail; null for non-billing members. */
  cancelAtPeriodEnd: boolean | null;
  lifetimeSessions: number;
  lifetimeFreeSessions: number;
  lifetimeFreeRemaining: number;
  monthlyIncludedSessions: number;
  currentPeriodSessions: number;
  /** Private financial detail; null for non-billing members. */
  currentPeriodBillableSessions: number | null;
  baseMonthlyUsd: number;
  additionalSessionUsd: number;
  /** Private financial detail; null for non-billing members. */
  currentPeriodEstimatedTotalUsd: number | null;
  paymentMethodPrompt: 'none' | 'one_remaining' | 'free_exhausted' | 'payment_failed';
  uploadAllowed: boolean;
  periodStartedAt: string;
  periodEndsAt: string;
}

export const USER_FEATURE_KEYS = [
  'maintenanceRecords',
  'vehicleSetupManagement',
  'parentCommunications',
  'advancedTelemetry',
  'aiCoaching',
  'prioritySupport',
] as const;

export type UserFeatureKey = (typeof USER_FEATURE_KEYS)[number];
export type UserFeatureOverrides = Partial<Record<UserFeatureKey, boolean>>;

export interface Team {
  id: string;
  name: string;
  slug: string;
  code: string;
  logoUrl: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
  phone: string;
  email: string;
  website: string | null;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  primaryLocation: string;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  country: string | null;
  state: string | null;
  postalCode: string;
  timezone: string;
  billingPlanId?: string | null;
  /** Purchased add-ons that extend plan entitlements (e.g. maintenance on Essentials). */
  billingAddons?: string[] | null;
  /** Stripe Customer id for team workspace billing. */
  stripeCustomerId?: string | null;
  /** Active Stripe subscription id for the team workspace, when provisioned. */
  stripeSubscriptionId?: string | null;
  /** First time this workspace started a Stripe trial; repeat checkout should not grant another trial. */
  stripeTrialStartedAt?: string | null;
  /** Partner / design-partner comp code id applied to this workspace. */
  partnerCodeId?: string | null;
  /** Denormalized label for the applied partner program. */
  partnerCodeLabel?: string | null;
  /** Set when a partner comp ends and a paid plan must be selected. */
  billingRequired?: boolean | null;
  ownerUserId?: string | null;
  /** Explicit current-member grants; contact email and roster roles do not grant finance access. */
  billingContactUserIds?: string[];
  /** Present on GET /teams/current for the authenticated viewer. */
  viewerIsWorkspaceOwner?: boolean;
  /** True when the roster has no team owner or coach staff (parent-managed family workspace). */
  isFamilyTeamWorkspace?: boolean;
  /** Family workspace this club team was created from (club teams only). */
  familyTeamId?: string | null;
  /** Active club team linked to this family workspace. */
  linkedClubTeamId?: string | null;
  /** Archived club team retained after downgrade for re-upgrade. */
  archivedClubTeamId?: string | null;
  /** Lifecycle for club teams spun up from a family workspace. */
  clubWorkspaceStatus?: 'active' | 'archived' | null;
  /** True when the workspace was auto-created from mobile upload and still needs web setup. */
  workspaceSetupPending?: boolean | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateTeamRequest {
  name: string;
  slug: string;
  phone: string;
  email: string;
  website?: string | null;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  primaryLocation: string;
  addressLine1: string;
  addressLine2?: string | null;
  city: string;
  country: string;
  state: string;
  postalCode: string;
  timezone: string;
}

export interface CreateTeamWorkspaceRequest {
  teamName: string;
  teamCode: string;
  role?: UserRole.TEAM_OWNER | UserRole.COACH | UserRole.PARENT | UserRole.DRIVER;
}

/** Admin-issued comp code (design partner, beta, etc.). */
export interface PartnerCode {
  id: string;
  code: string;
  label: string;
  active: boolean;
  maxRedemptions?: number | null;
  redeemedByTeamId?: string | null;
  redeemedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  revokedAt?: string | null;
}

export interface CreateTeamWorkspaceResponse {
  team: Team;
  user: User;
}

export type CompleteTeamWorkspaceRequest = CreateTeamWorkspaceRequest;

export interface TeamInvitationParentInput {
  name: string;
  email: string;
}

export interface TeamInvitationRequestedDriver {
  id: string;
  name: string;
  bringsOwnKart?: boolean;
}

export interface InviteTeamMemberRequest {
  name: string;
  email: string;
  role: UserRole;
  dateOfBirth?: string | null;
  parent?: TeamInvitationParentInput | null;
}

export interface TeamInvitation {
  id: string;
  teamId: string;
  source: TeamInvitationSource;
  requestedByUserId: string | null;
  name: string;
  email: string;
  role: UserRole;
  dateOfBirth: string | null;
  isMinor: boolean;
  parent: TeamInvitationParentInput | null;
  /** Family join requests: drivers the parent wants to register with the target team. */
  requestedDriverIds?: string[];
  requestedDrivers?: TeamInvitationRequestedDriver[];
  /** Family workspace the request is coming from. */
  homeTeamId?: string | null;
  inviteCode: string;
  status: TeamInvitationStatus;
  invitedAt: string;
  lastSentAt: string;
  sentCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTeamMemberRequest {
  name: string;
  email: string;
  role: UserRole.COACH | UserRole.MECHANIC | UserRole.PARENT;
  setupMode?: AccountSetupMode;
  password?: string;
}

export interface CreateTeamMemberResponse {
  user: User;
  accountSetupLinks: AccountSetupLink[];
}

export interface UpdateTeamMemberRolesRequest {
  role: UserRole;
  additionalRoles?: UserRole[];
}

export interface RequestTeamJoinDriverSelection {
  driverId: string;
  bringsOwnKart?: boolean;
}

export interface RequestTeamJoinRequest {
  teamCode: string;
  /** @deprecated Prefer `drivers` so each child can opt into bringing a family-owned kart. */
  driverIds?: string[];
  drivers?: RequestTeamJoinDriverSelection[];
}

export interface RequestTeamJoinResponse {
  team: Team;
  invitation: TeamInvitation;
}

export interface AcceptTeamInvitationResponse {
  invitation: TeamInvitation;
  user: User;
}

export interface DriverProfile {
  id: string;
  userId: string;
  teamId: string;
  driverNumber: string | null;
  dateOfBirth: string | null;
  nationality: string | null;
  weightKg: number | null;
  licenseNumber: string | null;
  karterSince: number | null;
  driverRating: number;
  personalBestLap: number | null;
  totalSessions: number;
  totalLaps: number;
  bio: string | null;
  createdAt: string;
  updatedAt: string;
  user: User;
}

export interface ParentProfile {
  id: string;
  userId: string;
  drivers: DriverProfile[];
  createdAt: string;
  updatedAt: string;
  user: User;
}

export interface CoachProfile {
  id: string;
  userId: string;
  teamId: string;
  specialties: string[];
  bio: string | null;
  createdAt: string;
  updatedAt: string;
  user: User;
}

export interface MechanicProfile {
  id: string;
  userId: string;
  teamId: string;
  specialties: string[];
  createdAt: string;
  updatedAt: string;
  user: User;
}

// ─── Kart ─────────────────────────────────────────────────────────────────────

export type VehicleType = 'kart' | 'other';

export interface Kart {
  id: string;
  teamId: string;
  vehicleType: VehicleType;
  kartNumber: string;
  nickname: string | null;
  status: KartStatus;
  qrCode: string;
  imageUrl: string | null;
  imagePath: string | null;
  kartClass: string | null;
  chassisBrand: string | null;
  chassisModel: string | null;
  chassisYear: number | null;
  chassisSerialNumber: string | null;
  engineBrand: string | null;
  engineModel: string | null;
  engineDisplacement: number | null;
  engineSerialNumber: string | null;
  engineHours: number;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface KartSetup {
  id: string;
  kartId: string;
  sessionId: string | null;
  driverId: string;
  frontTrackWidthMm: number | null;
  rearTrackWidthMm: number | null;
  frontCamberDeg: number | null;
  rearCamberDeg: number | null;
  castor: number | null;
  kingpinAngle: number | null;
  frontRideHeightMm: number | null;
  rearRideHeightMm: number | null;
  frontTirePressurePsi: number | null;
  rearTirePressurePsi: number | null;
  gearingDriver: number | null;
  gearingAxle: number | null;
  totalWeightKg: number | null;
  ballastKg: number | null;
  ballastPosition: string | null;
  notes: string | null;
  createdAt: string;
}

export interface KartAssignmentSummary {
  driverId: string;
  name: string;
  initials: string;
  rating: number;
  assignedSince: string | null;
  totalSessions: number;
  bestLapMs: number | null;
  bestLapFormatted: string | null;
}

export interface KartSetupSummary {
  frontTrackWidthMm: number | null;
  rearTrackWidthMm: number | null;
  seatPosition: string | null;
  gearRatioDriver: number | null;
  gearRatioAxle: number | null;
  frontTirePressurePsi: number | null;
  rearTirePressurePsi: number | null;
  totalWeightKg: number | null;
}

export interface KartImage {
  id: string;
  url: string;
  path: string;
  originalName: string;
  contentType: string;
  sizeBytes: number;
  uploadedAt: string;
}

export interface KartSetupHistoryEntry {
  id: string;
  setup: KartSetupSummary;
  customAttributes?: KartCustomAttribute[];
  notes: string | null;
  createdAt: string;
}

export interface UpdateKartSetupHistoryEntryRequest {
  setup?: Partial<KartSetupSummary>;
  customAttributes?: KartCustomAttributeInput[];
  notes?: string | null;
}

export interface KartMaintenanceSummary {
  label: string;
  intervalHours: number | null;
  remainingHours: number | null;
  progressPercent: number;
}

export type KartCustomAttributeType = 'number' | 'boolean' | 'string';

export interface KartCustomAttribute {
  id: string;
  name: string;
  type: KartCustomAttributeType;
  value: number | boolean | string | null;
}

export interface KartCustomAttributeInput {
  id?: string;
  name: string;
  type: KartCustomAttributeType;
  value: number | boolean | string | null;
}

export interface KartListItem extends Kart {
  assignedDriverId: string | null;
  assignedDriver: KartAssignmentSummary | null;
  tag: string | null;
  accentColor: string;
  secondaryColor: string;
  odometerKm: number;
  totalSessions: number;
  totalLaps: number;
  bestLapMs: number | null;
  bestLapFormatted: string | null;
  lastUsedAt: string | null;
  setup: KartSetupSummary;
  setupHistory: KartSetupHistoryEntry[];
  maintenance: KartMaintenanceSummary;
  images: KartImage[];
  customAttributes: KartCustomAttribute[];
}

export interface KartSummaryStats {
  totalKarts: number;
  activeKarts: number;
  maintenanceKarts: number;
  inactiveKarts: number;
}

export interface VehicleFieldTemplateFields {
  vehicleType: VehicleType;
  kartClass: string | null;
  tag: string | null;
  chassisBrand: string | null;
  chassisModel: string | null;
  chassisYear: number | null;
  engineBrand: string | null;
  engineModel: string | null;
  engineDisplacement: number | null;
  setup: KartSetupSummary;
  customAttributes: KartCustomAttribute[];
}

export interface VehicleFieldTemplate {
  id: string;
  teamId: string;
  name: string;
  fields: VehicleFieldTemplateFields;
  createdAt: string;
  updatedAt: string;
}

export interface CreateVehicleFieldTemplateRequest {
  name: string;
  sourceKartId: string;
}

export interface CreateKartRequest {
  vehicleType?: VehicleType;
  kartNumber?: string;
  nickname?: string | null;
  status?: KartStatus;
  assignedDriverId?: string | null;
  kartClass?: string | null;
  tag?: string | null;
  accentColor?: string;
  secondaryColor?: string;
  chassisBrand?: string | null;
  chassisModel?: string | null;
  chassisYear?: number | null;
  chassisSerialNumber?: string | null;
  engineBrand?: string | null;
  engineModel?: string | null;
  engineDisplacement?: number | null;
  engineSerialNumber?: string | null;
  engineHours?: number;
  odometerKm?: number;
  notes?: string | null;
  setup?: Partial<KartSetupSummary>;
  customAttributes?: KartCustomAttributeInput[];
}

export type UpdateKartRequest = Partial<CreateKartRequest>;

export interface UpdateKartStatusRequest {
  status: KartStatus;
}

export interface ListKartsQuery {
  status?: KartStatus | 'ALL';
  search?: string;
  vehicleType?: VehicleType | 'ALL';
  kartClass?: string;
  chassis?: string;
  engine?: string;
  tag?: string;
  page?: number;
  perPage?: number;
}

// ─── Session & Telemetry ──────────────────────────────────────────────────────

export interface Session {
  id: string;
  teamId: string;
  driverId: string;
  kartId: string | null;
  eventId: string | null;
  type: SessionType;
  trackName: string;
  trackLayout: string | null;
  date: string;
  conditions: string | null;
  temperatureCelsius: number | null;
  humidity: number | null;
  notes: string | null;
  bestLapMs: number | null;
  averageLapMs: number | null;
  totalLaps: number;
  createdAt: string;
  updatedAt: string;
  driver?: DriverProfile;
  laps?: Lap[];
}

export interface Lap {
  id: string;
  sessionId: string;
  lapNumber: number;
  lapTimeMs: number;
  sector1Ms: number | null;
  sector2Ms: number | null;
  sector3Ms: number | null;
  maxSpeedKph: number | null;
  minSpeedKph: number | null;
  avgSpeedKph: number | null;
  isPersonalBest: boolean;
  isSessionBest: boolean;
  isValid: boolean;
  telemetryData: TelemetryPoint[] | null;
}

export interface TelemetryPoint {
  timeMs: number;
  speedKph: number;
  rpm: number | null;
  throttle: number | null;
  brake: number | null;
  steeringAngle: number | null;
  lateralG: number | null;
  longitudinalG: number | null;
  gpsLat: number | null;
  gpsLon: number | null;
}

// ─── Data Files ───────────────────────────────────────────────────────────────

export interface MyChronDataSummary {
  format: string | null;
  compressedBytes: number | null;
  decodedBytes: number | null;
  topLevelKeys: string[];
  lapCount: number | null;
  channelCount: number | null;
  sampleCount: number | null;
  trackName: string | null;
  driverName: string | null;
  kartNumber: string | null;
  sessionStartedAt: string | null;
  /** Logger session start from MyChron metadata, UTC epoch ms. */
  recordedAtMs: number | null;
}

export interface MyChronChannelSummary {
  shortName: string;
  longName: string | null;
  unit: string | null;
  samplePeriodMs: number | null;
  sampleCount: number;
  minValue: number | null;
  maxValue: number | null;
  firstTimeMs: number | null;
  lastTimeMs: number | null;
}

export interface MyChronLapSummary {
  lapNumber: number;
  segment: number | null;
  durationMs: number | null;
  endTimeMs: number | null;
}

export interface MyChronSeriesPoint {
  timeMs: number;
  value: number;
}

export interface MyChronPreviewSeries {
  shortName: string;
  longName: string | null;
  unit: string | null;
  points: MyChronSeriesPoint[];
}

export interface MyChronMapPoint {
  timeMs: number;
  lat: number;
  lon: number;
  speedKph: number | null;
}

export interface DataFileListItem {
  /** Payer pinned at publication, independently of personal/team storage ownership. */
  billingPayer?: { ownerKind: UsageBillingOwnerKind; ownerId: string };
  id: string;
  teamId: string;
  /** Authoritative owner. Personal sessions may be shared into teamId without becoming team property. */
  ownerKind?: UsageBillingOwnerKind;
  ownerId?: string;
  sharedWithTeamIds?: string[];
  source: DataFileSource;
  originalName: string;
  contentType: string;
  sizeBytes: number;
  storagePath: string;
  rawOriginalName?: string | null;
  rawContentType?: string | null;
  rawSizeBytes?: number | null;
  rawStoragePath?: string | null;
  rawDownloadUrl?: string | null;
  /** Installation-specific durable upload attempt used to reconcile ambiguous client retries. */
  clientUploadId?: string | null;
  downloadUrl: string;
  summary: MyChronDataSummary;
  channels: MyChronChannelSummary[];
  laps: MyChronLapSummary[];
  previewSeries: MyChronPreviewSeries[];
  mapPreview: MyChronMapPoint[];
  /** Team-specific saved assignment used when presenting this telemetry session. */
  sessionAssignment?: {
    driverId: string | null;
    driverName: string | null;
  };
  sessionContextSummary?: {
    kartId?: string | null;
    trackId: string | null;
    trackName?: string | null;
    trackLocation?: string | null;
    contributionId: string | null;
    circuitLapCount?: number;
    circuitBestLapMs?: number | null;
  };
  driverId: string | null;
  kartId: string | null;
  uploadedByUserId: string;
  uploadedAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface DataFileSummaryStats {
  totalFiles: number;
  totalSizeBytes: number;
  latestUploadAt: string | null;
  myChronFiles: number;
  alfanoFiles: number;
}

export interface ListDataFilesQuery {
  source?: DataFileSource | 'ALL';
  search?: string;
  page?: number;
  perPage?: number;
  detail?: 'full' | 'summary' | 'session-list';
}

export interface DataFileHistoryQuery extends ListDataFilesQuery {
  snapshot?: string;
}

export interface DataFileHistoryPage extends PaginatedResponse<DataFileListItem> {
  snapshot: string;
}

export interface UploadDataFileResponse {
  file: DataFileListItem;
  workspaceUser?: User;
}

// ─── Telemetry tracks & session context ───────────────────────────────────────

export interface SavedTrack {
  id: string;
  teamId: string;
  name: string;
  address?: string | null;
  locality?: string | null;
  region?: string | null;
  countryCode?: string | null;
  centerLat: number;
  centerLon: number;
  archivedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSavedTrackRequest {
  name: string;
  address?: string | null;
  locality?: string | null;
  region?: string | null;
  countryCode?: string | null;
  centerLat: number;
  centerLon: number;
}

export interface CircuitLocation {
  address?: string | null;
  locality?: string | null;
  region?: string | null;
  countryCode?: string | null;
  centerLat: number;
  centerLon: number;
}

export interface CircuitPreferences {
  favoriteTrackIds: string[];
  updatedAt: string | null;
}

export interface UpdateCircuitPreferencesRequest {
  favoriteTrackIds: string[];
}

export type SavedCircuitLapKind = 'flying' | 'out' | 'in' | 'crash';

export interface SavedCircuitLapWindow {
  lapNumber: number;
  startMs: number;
  endMs: number;
  durationSec: number;
  kind: SavedCircuitLapKind;
  complete: boolean;
}

export interface SavedCircuitConfigurationIdentity {
  version: 1;
  contributionId: string;
  contributionUpdatedAt: string;
  /** Collision-proof contribution revision used to bind derived lap windows. */
  contributionRevision?: string;
  trackId: string;
  layoutName: string | null;
  direction: 'clockwise' | 'counterclockwise';
  startFinishLine: {
    pointA: { lat: number; lon: number };
    pointB: { lat: number; lon: number };
  };
}

export interface AuthoredGeometryIdentity {
  version: 1;
  fingerprint: string;
}

export interface SavedCircuitLapSet {
  contributionId: string;
  circuitName: string;
  savedAt: string;
  /** Immutable circuit version used to produce these lap windows. */
  configuration?: SavedCircuitConfigurationIdentity;
  /** Server-derived identity of the full authored geometry used for corner evidence. */
  authoredGeometry?: AuthoredGeometryIdentity;
  laps: SavedCircuitLapWindow[];
}

export type CornerSetUnavailableReason =
  | 'not-found'
  | 'no-assigned-configuration'
  | 'missing-authoritative-geometry'
  | 'invalid-authored-geometry'
  | 'no-qualifying-historical-contexts'
  | 'resource-limit';

export type CornerUnavailableReason =
  | 'no-complete-lap'
  | 'missing-corner-boundaries'
  | 'ambiguous-corner-boundaries'
  | 'no-sustained-braking-onset'
  | 'missing-following-straight-boundary'
  | 'insufficient-gps-samples'
  | 'invalid-gps-samples'
  | 'ineligible-sampling-cadence'
  | 'reverse-or-repeated-crossing'
  | 'no-eligible-source';

export type CornerConfidenceReason = 'irregular-sampling' | 'recorded-date-unavailable';

export interface ObservedGpsPoint {
  timecodeMs: number;
  lat: number;
  lon: number;
  speedKph: number;
}

export interface BestObservedCornerIdentity {
  id: string;
  turnNumber: number;
  label: string;
}

export type BestObservedCornerReference =
  | {
      status: 'available';
      corner: BestObservedCornerIdentity;
      source: {
        sessionKey: string;
        dataFileId: string;
        lapNumber: number;
        recordedAtMs: number | null;
      };
      window: {
        brakingOnsetMs: number;
        cornerEntryMs: number;
        minimumSpeedMs: number;
        cornerExitMs: number;
        followingStraightEndMs: number;
        durationSec: number;
      };
      braking: {
        severity: 'soft' | 'hard';
        onsetSpeedKph: number;
        peakDecelerationG: number;
        points: ObservedGpsPoint[];
      };
      speeds: {
        entryKph: number;
        minimumKph: number;
        exitKph: number;
      };
      followingStraight: {
        endSpeedKph: number;
        speedGainKph: number;
        elapsedSec: number;
      };
      observedLine: ObservedGpsPoint[];
      confidence: {
        level: 'high' | 'medium' | 'low';
        reasons: CornerConfidenceReason[];
      };
      observations: Array<{
        code: 'braking-onset' | 'minimum-speed' | 'exit-to-straight-result';
        text: string;
      }>;
    }
  | {
      status: 'unavailable';
      corner: BestObservedCornerIdentity;
      reason: CornerUnavailableReason;
    };

export interface BestObservedCornerReferences {
  status: 'available' | 'partial' | 'unavailable';
  driverId: string | null;
  configuration: SavedCircuitConfigurationIdentity | null;
  authoredGeometry: AuthoredGeometryIdentity | null;
  reason: CornerSetUnavailableReason | null;
  corners: BestObservedCornerReference[];
}

export interface IdealLineReference {
  status: 'available' | 'unavailable';
  driverId: string | null;
  configuration: SavedCircuitConfigurationIdentity | null;
  source: {
    sessionKey: string;
    dataFileId: string;
    lapNumber: number;
    durationSec: number;
    recordedAtMs: number | null;
  } | null;
  points: Array<{
    timecodeMs: number;
    lat: number;
    lon: number;
    speed: number;
  }>;
}

export interface SessionLapTimingWindow {
  lapNumber: number;
  startMs: number;
  endMs: number;
  durationSec: number;
  utcStartIso: string | null;
  utcEndIso: string | null;
  kind?: SavedCircuitLapKind;
  complete?: boolean;
}

export interface SessionLapTimingOverlay {
  generatedAt: string;
  anchorMs: number | null;
  anchorIso: string | null;
  rawLaps: SessionLapTimingWindow[];
  circuitLapSet: {
    contributionId: string;
    circuitName: string;
    savedAt: string;
    laps: SessionLapTimingWindow[];
  } | null;
}

export type SessionCircuitMatchStatus =
  | 'automatic_match_confirmed'
  | 'manual_match_confirmed'
  | 'automatic_match_failed';

export interface SessionCircuitMatchOutcome {
  status: SessionCircuitMatchStatus;
  evaluatedAt: string;
  candidateCount: number;
  representativeLapNumber: number | null;
  representativeLapDurationMs: number | null;
}

export interface SessionLapSetupAssignment {
  lapTimingMode: 'mychron' | 'circuit';
  lapNumber: number;
  kartId: string;
  kartSetupHistoryEntryId: string;
}

export interface TelemetrySessionContext {
  sessionKey: string;
  teamId: string;
  dataFileId: string | null;
  driverId: string | null;
  /** True after the driver field has been explicitly assigned or cleared. */
  driverAssignmentSet?: boolean;
  kartId: string | null;
  kartSetupHistoryEntryId: string | null;
  /** Per-lap setup snapshots. Missing entries inherit the session setup. */
  lapSetupAssignments?: SessionLapSetupAssignment[];
  trackId: string | null;
  /** Saved circuit contribution used for overlay, S/F laps, and lap timing. */
  contributionId: string | null;
  recordedAtMs: number | null;
  excludedLaps?: LapExclusion[];
  /** Outcome recorded by the upload-time circuit verification flow. */
  circuitMatch?: SessionCircuitMatchOutcome | null;
  circuitLapSet?: SavedCircuitLapSet | null;
  lapTimingOverlay?: SessionLapTimingOverlay | null;
  createdAt: string;
  updatedAt: string;
}

export type LapExclusionReason = 'out' | 'in' | 'invalid' | 'other';

export interface LapExclusion {
  lapNumber: number;
  reason: LapExclusionReason;
}

export interface UpdateTelemetrySessionContextRequest {
  dataFileId?: string | null;
  driverId?: string | null;
  kartId?: string | null;
  kartSetupHistoryEntryId?: string | null;
  lapSetupAssignments?: SessionLapSetupAssignment[];
  trackId?: string | null;
  contributionId?: string | null;
  recordedAtMs?: number | null;
  excludedLaps?: LapExclusion[];
  circuitMatch?: SessionCircuitMatchOutcome | null;
  circuitLapSet?: SavedCircuitLapSet | null;
  lapTimingOverlay?: SessionLapTimingOverlay | null;
}

// ─── Leaderboard ──────────────────────────────────────────────────────────────

export interface LeaderboardEntry {
  sessionKey: string;
  lapNumber: number | null;
  lapTimeMs: number;
  recordedAtMs: number | null;
  driverId: string | null;
  driverName: string | null;
  kartId: string | null;
  kartLabel: string | null;
  setup: KartSetupSummary | null;
  setupCapturedAt: string | null;
}

export interface LeaderboardCircuit {
  circuitKey: string;
  circuitName: string;
  sessionCount: number;
  bestLapMs: number;
  lastActivityMs: number | null;
  entries: LeaderboardEntry[];
}

export interface TelemetrySegment {
  id: string;
  teamId: string;
  name: string;
  sessionKey: string;
  dataFileId: string | null;
  trackId: string | null;
  lapNumber: number;
  startSec: number;
  endSec: number;
  startTimecodeMs: number;
  endTimecodeMs: number;
  durationSec: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTelemetrySegmentRequest {
  name: string;
  sessionKey: string;
  dataFileId?: string | null;
  trackId?: string | null;
  lapNumber: number;
  startSec: number;
  endSec: number;
  startTimecodeMs: number;
  endTimecodeMs: number;
}

// ─── Track contributions (community track enrichment) ─────────────────────────

export type TrackContributionStatus =
  | 'draft'
  | 'pending'
  | 'reviewed'
  | 'accepted'
  | 'rejected'
  | 'merged';

export interface TrackContributionGpsCoord {
  lat: number;
  lon: number;
}

export interface TrackContributionCornerApex {
  id: string;
  label: string;
  lat: number;
  lon: number;
  turnNumber: number;
  confidence: number;
  curveType?: string;
  detectionProfile?: string;
}

export interface TrackContributionStartLine {
  pointA: TrackContributionGpsCoord;
  pointB: TrackContributionGpsCoord;
  confidence: number;
}

export interface TrackContributionSegment {
  id: string;
  label: string;
  startFraction: number;
  endFraction: number;
  confidence: number;
}

export interface DetailedCircuitFeature {
  id: string;
  parentId?: string | null;
  type: string;
  label: string;
  polygonRings?: TrackContributionGpsCoord[][];
  entryLine?: TrackContributionGpsCoord[];
  exitLine?: TrackContributionGpsCoord[];
  directionalPoint?: TrackContributionGpsCoord | null;
  apexPoint?: TrackContributionGpsCoord | null;
  bounds?: TrackContributionGpsCoord[][];
}

export interface DetailedCircuitGeometry {
  version: 1;
  outlineRings: TrackContributionGpsCoord[][];
  centerLine?: TrackContributionGpsCoord[];
  features: DetailedCircuitFeature[];
}

export interface TrackContributionPayload {
  trackId: string;
  gpsPolyline: TrackContributionGpsCoord[];
  startLineSuggestion: TrackContributionStartLine;
  segmentSuggestions: TrackContributionSegment[];
  cornerApexSuggestions: TrackContributionCornerApex[];
  direction: 'clockwise' | 'counterclockwise';
  lapLengthEstimateM: number;
  confidenceScore: number;
  startFraction?: number;
  /** Full-fidelity circuit shapes, hydrated only on contribution detail responses. */
  detailedGeometry?: DetailedCircuitGeometry;
  detailedGeometryVersion?: 1;
  detailedGeometryChunkCount?: number;
  detailedGeometryFeatureCount?: number;
  /** Server-owned, collision-proof revision for the detailed geometry snapshot. */
  detailedGeometryRevision?: string;
  /** Present on summary list responses when geometry arrays are omitted. */
  cornerCount?: number;
  polylinePointCount?: number;
}

export interface ListTrackContributionsQuery {
  status?: TrackContributionStatus;
  detail?: 'summary' | 'full';
  search?: string;
  page?: number;
  perPage?: number;
}

export interface TrackContribution {
  id: string;
  teamId: string;
  trackId: string;
  sessionKey: string;
  venue: string | null;
  layoutName?: string | null;
  location?: CircuitLocation | null;
  notes?: string | null;
  status: TrackContributionStatus;
  payload: TrackContributionPayload;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
}

export interface SaveTrackContributionRequest {
  sessionKey: string;
  /** Stable draft key; omit to upsert the session's default draft, or pass a new uuid for a new draft. */
  draftId?: string;
  venue?: string | null;
  layoutName?: string | null;
  location?: CircuitLocation | null;
  notes?: string | null;
  status?: TrackContributionStatus;
  payload: TrackContributionPayload;
}

// ─── Maintenance ──────────────────────────────────────────────────────────────

export interface MaintenanceRecord {
  id: string;
  kartId: string;
  mechanicId: string | null;
  category: MaintenanceCategory;
  status: MaintenanceStatus;
  description: string;
  partsUsed: string[];
  cost: number | null;
  scheduledDate: string | null;
  completedDate: string | null;
  engineHoursAtService: number | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface InventoryItem {
  id: string;
  teamId: string;
  name: string;
  category: string;
  partNumber: string | null;
  quantity: number;
  minQuantity: number;
  unitCost: number | null;
  supplier: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

// ─── Events & Schedule ────────────────────────────────────────────────────────

export interface Event {
  id: string;
  teamId: string;
  name: string;
  type: EventType;
  trackName: string;
  location: string | null;
  startDate: string;
  endDate: string;
  registrationDeadline: string | null;
  description: string | null;
  website: string | null;
  classes: string[];
  createdAt: string;
  updatedAt: string;
  attendees?: EventAttendee[];
}

export interface EventAttendee {
  id: string;
  eventId: string;
  userId: string;
  attending: boolean;
  kartClass: string | null;
  notes: string | null;
}

// ─── Coaching ─────────────────────────────────────────────────────────────────

export interface CoachingNote {
  id: string;
  coachId: string;
  driverId: string;
  sessionId: string | null;
  content: string;
  focusAreas: string[];
  sentiment: 'POSITIVE' | 'NEUTRAL' | 'NEEDS_WORK';
  isPrivate: boolean;
  createdAt: string;
  updatedAt: string;
  coach?: CoachProfile;
}

export interface DrillAssignment {
  id: string;
  coachId: string;
  driverId: string;
  category: DrillCategory;
  title: string;
  description: string;
  videoUrl: string | null;
  dueDate: string | null;
  completed: boolean;
  completedDate: string | null;
  driverNotes: string | null;
  createdAt: string;
}

export interface DevelopmentPlan {
  id: string;
  coachId: string;
  driverId: string;
  season: number;
  goals: string[];
  strengths: string[];
  areasForImprovement: string[];
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

// ─── Achievements ─────────────────────────────────────────────────────────────

export interface Achievement {
  id: string;
  driverId: string;
  type: AchievementType;
  title: string;
  description: string;
  iconUrl: string | null;
  earnedAt: string;
  metadata: Record<string, unknown>;
}

export interface Goal {
  id: string;
  driverId: string;
  title: string;
  description: string | null;
  targetValue: number | null;
  currentValue: number | null;
  unit: string | null;
  targetDate: string | null;
  completed: boolean;
  completedDate: string | null;
  createdAt: string;
  updatedAt: string;
}

// ─── API Responses ────────────────────────────────────────────────────────────

export interface ApiResponse<T> {
  data: T;
  message?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  perPage: number;
  totalPages: number;
}

export interface ApiError {
  error: string;
  message: string;
  statusCode: number;
}

// ─── Release Notes ────────────────────────────────────────────────────────────

export interface ReleaseNote {
  releaseKey: string;
  title: string;
  publishedAt: string;
  summary?: string | null;
  items: string[];
}

export interface CurrentReleaseNoteResponse {
  releaseNotes: ReleaseNote[];
}

export interface DismissReleaseNoteResponse {
  dismissed: boolean;
  releaseKey: string;
}

// ─── Help articles ────────────────────────────────────────────────────────────

export interface HelpArticleSummary {
  id: string;
  slug: string;
  categoryId: HelpArticleCategoryId;
  title: string;
  description: string;
  videoTitle?: string | null;
  loomId?: string | null;
  publishedAt: string | null;
  sortOrder: number;
  updatedAt: string;
}

export interface HelpArticleDetail extends HelpArticleSummary {
  bodyMarkdown: string;
  bullets: string[];
  createdAt: string;
}

export interface HelpArticleImageUploadResponse {
  assetId: string;
  url: string;
  markdown: string;
  originalName: string;
}

// ─── Admin ───────────────────────────────────────────────────────────────────

export interface AdminTeamSummary {
  id: string;
  name: string;
  code: string;
  ownerEmail: string | null;
  createdAt: string;
  billingPlanId: string | null;
  billingStatus: string;
  stripeSubscriptionId: string | null;
  stripeCustomerId: string | null;
  partnerCodeLabel: string | null;
  billingRequired: boolean;
  users: number;
  activeUsers: number;
  drivers: number;
  vehicles: number;
  estimatedMonthlyTotal: number;
}

export interface AdminUsageBillingAccount {
  id: string;
  ownerKind: UsageBillingOwnerKind;
  ownerId: string;
  ownerLabel: string;
  billingMode?: 'usage' | 'fixed_monthly';
  fixedAccessReason?: string | null;
  fixedUploadAllowed?: boolean;
  collectionMethod?: 'charge_automatically' | 'send_invoice';
  payerEmail: string | null;
  status: UsageBillingStatus;
  hasPaymentMethod: boolean;
  hasActiveSubscription: boolean;
  payingThisMonth: boolean;
  currentPeriodSessions: number;
  currentPeriodBillableSessions: number;
  currentPeriodEstimatedTotalUsd: number;
  periodStartedAt: string;
  periodEndsAt: string;
  lifetimeSessions: number;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
}

export interface AdminUserSummary {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  additionalRoles?: UserRole[];
  teamId: string | null;
  teamName: string | null;
  accountDisabled: boolean;
  emailVerified: boolean;
  stripeCustomerId: string | null;
  lastLoginAt: string | null;
  lastActivityAt: string | null;
  createdAt: string;
  featureOverrides: UserFeatureOverrides;
}

export interface AdminMonthlyMetric {
  month: string;
  teamsCreated: number;
  usersCreated: number;
  estimatedMrr: number;
}

export interface AdminCircuitCandidate {
  id: string;
  teamId: string;
  teamName: string | null;
  sessionKey: string;
  dataFileId: string | null;
  suggestedName: string;
  centerLat: number | null;
  centerLon: number | null;
  lapCount: number;
  trackId: string | null;
  contributionId: string | null;
  updatedAt: string;
}

export interface AdminCircuitSummary {
  id: string;
  teamId: string;
  teamName: string | null;
  name: string;
  centerLat: number;
  centerLon: number;
  sessionCount: number;
  contributionCount: number;
  archivedAt: string | null;
  updatedAt: string;
}

export interface AdminCircuitContributionSummary {
  id: string;
  teamId: string;
  teamName: string | null;
  trackId: string;
  sessionKey: string;
  venue: string | null;
  status: TrackContributionStatus;
  direction: 'clockwise' | 'counterclockwise' | null;
  lapLengthEstimateM: number | null;
  confidenceScore: number | null;
  pointCount: number;
  cornerCount: number;
  updatedAt: string;
}

export interface AdminReceivedEmailAttachmentSummary {
  id: string;
  filename: string | null;
  contentType: string;
  contentDisposition: string | null;
  contentId: string | null;
}

export interface AdminReceivedEmailSummary {
  id: string;
  eventCreatedAt: string | null;
  emailCreatedAt: string;
  from: string;
  to: string[];
  cc: string[];
  bcc: string[];
  receivedFor: string[];
  messageId: string;
  subject: string;
  attachmentCount: number;
  attachments: AdminReceivedEmailAttachmentSummary[];
  source: 'webhook' | 'resend_api';
  updatedAt: string;
}

export interface AdminSentEmailSummary {
  id: string;
  emailCreatedAt: string;
  from: string;
  to: string[];
  cc: string[];
  bcc: string[];
  replyTo: string | null;
  messageId: string | null;
  subject: string;
  text: string;
  html: string;
  kind: 'customer_communication' | 'reply';
  relatedReceivedEmailId: string | null;
  updatedAt: string;
}

export type AdminEmailDraftStatus = 'draft' | 'sending' | 'dismissed' | 'sent' | 'failed';

export interface AdminEmailDraft {
  id: string;
  recipientEmail: string;
  recipientName: string | null;
  userId: string | null;
  subject: string;
  body: string;
  reason: string;
  lifecycleStage: string;
  intendedOutcome: string;
  evidence: string[];
  confidence: 'low' | 'medium' | 'high';
  source: string;
  status: AdminEmailDraftStatus;
  createdAt: string;
  updatedAt: string;
  reviewedAt: string | null;
  reviewedByUserId: string | null;
  sentAt: string | null;
  resendMessageId: string | null;
  failureMessage: string | null;
}

export interface AdminUnsupportedUpload {
  id: string;
  teamId: string;
  teamName: string | null;
  originalName: string;
  contentType: string;
  sizeBytes: number;
  uploadedByUserId: string;
  uploadedAt: string;
  createdAt: string;
  updatedAt: string;
  downloadUrl: string;
}

export interface AdminReceivedEmailDetail extends AdminReceivedEmailSummary {
  text: string | null;
  html: string | null;
  sentEmails: AdminSentEmailSummary[];
}

export interface AdminUnmatchedSession {
  id: string;
  teamId: string;
  teamName: string | null;
  sessionKey: string;
  dataFileId: string;
  originalName: string;
  trackName: string | null;
  driverName: string | null;
  kartNumber: string | null;
  recordedAtMs: number | null;
  uploadedAt: string;
  updatedAt: string;
  lapCount: number | null;
  gpsPointCount: number;
  gpsAvailable: boolean;
  sampleCount: number | null;
  contextTrackId: string | null;
  contextContributionId: string | null;
  circuitMatch: SessionCircuitMatchOutcome | null;
  reason: string;
  gpsCenterLat: number | null;
  gpsCenterLon: number | null;
  nearestTrackId: string | null;
  nearestTrackName: string | null;
  nearestTrackDistanceKm: number | null;
  assignedTrackDistanceKm: number | null;
  alertType:
    | 'far_from_nearest_track'
    | 'no_circuit_assignment'
    | 'far_from_assigned_track'
    | 'automatic_circuit_match_failed';
  isRecent: boolean;
}

export interface AdminUnmatchedSessionsResponse {
  total: number;
  recentTotal: number;
  sessions: AdminUnmatchedSession[];
  page: number;
  perPage: number;
  totalPages: number;
}

export interface AdminOverviewResponse {
  totals: {
    teams: number;
    users: number;
    activeUsers: number;
    activeSubscriptions: number;
    partnerCompTeams: number;
    estimatedMrr: number;
    receivedEmails: number;
    sentEmails: number;
  };
  monthly: AdminMonthlyMetric[];
  teams: AdminTeamSummary[];
  users: AdminUserSummary[];
  circuits: AdminCircuitSummary[];
  circuitContributions: AdminCircuitContributionSummary[];
  circuitCandidates: AdminCircuitCandidate[];
  unsupportedUploads: AdminUnsupportedUpload[];
  receivedEmails: AdminReceivedEmailSummary[];
  sentEmails: AdminSentEmailSummary[];
}

export interface AdminStatsSummary {
  totals: {
    teams: number;
    users: number;
    activeUsers: number;
    activeSubscriptions: number;
    partnerCompTeams: number;
    billingReviewTeams: number;
    estimatedMrr: number | null;
    circuits: number;
    circuitContributions: number;
    unsupportedUploads: number;
    receivedEmails: number;
    sentEmails: number;
  };
  monthly: AdminMonthlyMetric[];
}

export interface AdminUnmatchedSessionsSummary {
  total: number;
  recentTotal: number;
}

export interface ListAdminPaginationQuery {
  page?: number;
  perPage?: number;
}

export interface ListAdminUsersQuery extends ListAdminPaginationQuery {
  search?: string;
  teamId?: string;
  role?: UserRole | 'ALL';
  status?: 'ALL' | 'ACTIVE' | 'UNVERIFIED' | 'DISABLED';
}

export interface ListAdminTeamsQuery extends ListAdminPaginationQuery {
  search?: string;
}

// ─── Auth ─────────────────────────────────────────────────────────────────────

export interface KioskAccountSessionClaim {
  version: 1;
  stationId: string;
  provisioningGeneration: number;
  associationId: string;
  associationGeneration: number;
  accountSessionId: string;
  accountSessionGeneration: number;
  browserLeaseId: string;
  browserLeaseGeneration: number;
}

export interface AuthTokenPayload {
  sub: string;
  email: string;
  role: UserRole;
  additionalRoles?: UserRole[];
  teamId: string | null;
  supportMasqueradeAllowed?: boolean;
  masqueradeActor?: SupportMasqueradeActor;
  kioskSession?: KioskAccountSessionClaim;
  iat: number;
  exp: number;
}

export interface SupportMasqueradeActor {
  id: string;
  email: string;
  name: string;
}

export interface SupportMasqueradeTeamOption {
  id: string;
  name: string;
  code: string;
}

export interface SupportMasqueradeUserOption {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  teamId: string | null;
  teamName: string | null;
}

export interface SupportMasqueradeOptionsResponse {
  teams: SupportMasqueradeTeamOption[];
  users: SupportMasqueradeUserOption[];
}

export interface StartSupportMasqueradeRequest {
  userId: string;
  reason: string;
}

export interface SupportMasqueradeResponse extends AuthResponse {
  actor: SupportMasqueradeActor;
}

export type FeedbackIssueType = 'bug' | 'feature' | 'task' | 'question';
export type FeedbackIssueSeverity = 'low' | 'medium' | 'high' | 'critical';

export interface CreateFeedbackIssueRequest {
  type: FeedbackIssueType;
  title: string;
  area?: string | null;
  severity?: FeedbackIssueSeverity;
  stepsToReproduce?: string | null;
  expectedBehavior?: string | null;
  actualBehavior?: string | null;
  notes?: string | null;
  currentUrl?: string | null;
  browser?: string | null;
}

export interface FeedbackIssueAttachment {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  storagePath: string;
  url: string;
}

export interface FeedbackIssueResponse {
  id: string;
  markdown: string;
  attachments: FeedbackIssueAttachment[];
  submitted: boolean;
}

export interface AdminFeedbackReport extends CreateFeedbackIssueRequest {
  id: string;
  severity: FeedbackIssueSeverity;
  reporter: {
    id: string;
    name: string;
    email: string;
    teamId: string | null;
    role: string;
  };
  attachments: FeedbackIssueAttachment[];
  status: string;
  githubSyncStatus: string;
  githubIssueNumber: number | null;
  githubIssueUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  name: string;
  role: UserRole;
  teamName?: string;
  teamCode?: string;
  requestedTeamCode?: string;
}

export interface CompleteRegistrationRequest {
  setupToken: string;
  password: string;
}

export interface GoogleAuthRequest {
  idToken: string;
  role?: UserRole;
  teamName?: string;
  teamCode?: string;
  requestedTeamCode?: string;
}

export interface GoogleAuthSetupRequiredResponse {
  setupRequired: true;
  email: string;
  name: string;
  avatarUrl: string | null;
}

export interface AppleAuthRequest {
  idToken: string;
  email?: string | null;
  name?: string | null;
  role?: UserRole;
  teamName?: string;
  teamCode?: string;
  requestedTeamCode?: string;
}

export interface AppleAuthSetupRequiredResponse {
  setupRequired: true;
  email: string;
  name: string;
  avatarUrl: null;
}

export interface AuthResponse {
  token: string;
  user: User;
}

// ─── MyChron / Telemetry Import ───────────────────────────────────────────────

export interface MyChronImportResult {
  sessionId: string;
  lapsImported: number;
  bestLapMs: number;
  averageLapMs: number;
  errors: string[];
}

export type MyChronSourceState =
  | 'intent_created'
  | 'uploading'
  | 'uploaded_unverified'
  | 'verifying'
  | 'verified'
  | 'conflict'
  | 'failed'
  | 'logically_deleted'
  | 'physically_deleted';

export type MyChronCustodyFacetState = 'pending' | 'verified' | 'failed';
export type MyChronUsageFacetState = 'pending' | 'recorded' | 'correction_pending';
export type MyChronCleanupEligibility = 'blocked' | 'ready';

export interface MyChronSourceUploadIntentRequest {
  custodyId: string;
  dataFileId: string;
  sessionId: string;
  originalName: string;
  contentType: string;
  byteLength: number;
  sha256: string;
  crc32c: string;
  parserVersion: string;
  normalizerVersion: string;
  parsingInputFingerprint: string;
  sourceFormatValidatorVersion: string;
  authorizationGeneration: number;
  actionId: string;
}

export interface MyChronSourceUploadIntent {
  custodyId: string;
  sourceId: string;
  sourceGeneration: number;
  aggregateGeneration: number;
  uploadAttemptId: string;
  state: MyChronSourceState;
  uploadedBytes: number;
  expectedBytes: number;
  fingerprint: string;
  createdAt: string;
  updatedAt: string;
}

export interface MyChronSourceReceiptProjection {
  receiptVersion: 1;
  custodyId: string;
  sourceId: string;
  sourceGeneration: number;
  aggregateGeneration: number;
  dataFileId: string;
  sessionId: string;
  originalName: string;
  extension: 'xrz' | 'xrk' | 'hrz' | 'hrk' | 'drk';
  contentType: string;
  byteLength: number;
  sha256: string;
  crc32c: string;
  parserVersion: string;
  normalizerVersion: string;
  parsingInputFingerprint: string;
  sourceFormatValidatorVersion: string;
  retentionPolicyVersion: string;
  deletionPolicyVersion: string;
  verifiedAt: string;
  sourceStatus: 'verified' | 'logically_deleted' | 'physically_deleted';
  originalSourceStatus: 'verified' | 'Original source unavailable';
}

export interface MyChronCloudSafeReceipt {
  receiptVersion: 1;
  custodyId: string;
  aggregateGeneration: number;
  sourceReceiptId: string;
  custodyTransferReceiptId?: string;
  sourceGeneration: number;
  derivedGeneration: number;
  contextGeneration: number;
  indexReceiptId: string;
  issuedAt: string;
}

export interface MyChronSourceStatusProjection {
  custodyId: string;
  dataFileId: string;
  sessionId: string;
  aggregateGeneration: number;
  sourceState: MyChronSourceState;
  sourceGeneration: number;
  derivedSession: MyChronCustodyFacetState;
  sessionContext: MyChronCustodyFacetState;
  requiredIndex: MyChronCustodyFacetState;
  usage: MyChronUsageFacetState;
  cleanupEligibility: MyChronCleanupEligibility;
  sourceReceipt: MyChronSourceReceiptProjection | null;
  cloudSafeReceipt: MyChronCloudSafeReceipt | null;
  originalSourceStatus: 'verified' | 'Original source unavailable';
}

// ─── Drivers ──────────────────────────────────────────────────────────────────

export interface DriverParentSummary {
  id: string;
  userId: string;
  name: string;
  email: string;
}

export interface DriverListItem {
  id: string;
  /** Historical profiles have no login account. */
  userId: string | null;
  profileKind?: 'historical';
  teamId: string;
  name: string;
  email: string;
  username?: string | null;
  initials: string;
  avatarUrl: string | null;
  driverNumber: string | null;
  licenseNumber: string | null;
  dateOfBirth: string | null;
  age: number | null;
  category: DriverCategory;
  driverRating: number;
  personalBestLap: number | null;
  bestLapFormatted: string | null;
  bestLapDate: string | null;
  avgLapMs: number | null;
  avgLapFormatted: string | null;
  totalSessions: number;
  sessionsThisMonth: number;
  status: DriverStatus;
  sparkline: number[];
  parents: DriverParentSummary[];
  /** True when the driver account is directly managed by the team rather than by a parent or the driver themselves. */
  teamManaged: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DriverKartAssignment {
  kartId: string;
  driverId: string;
  kartNumber: string;
  nickname: string | null;
}

export interface DriverDirectoryResponse {
  drivers: DriverListItem[];
  kartAssignments: DriverKartAssignment[];
}

export interface DriverSummaryStats {
  totalDrivers: number;
  activeDrivers: number;
  averageRating: number;
  sessionsThisMonth: number;
  bestLapAllTime: number | null;
  bestLapDriverName: string | null;
}

export interface CreateDriverParentInput {
  name: string;
  email: string;
  setupMode?: AccountSetupMode;
  password?: string;
}

export interface LinkDriverParentRequest extends CreateDriverParentInput {
  applyToAllLinkedDrivers?: boolean;
}

export interface CreateDriverRequest {
  name: string;
  email?: string;
  username?: string;
  setupMode?: AccountSetupMode;
  password?: string;
  dateOfBirth: string;
  driverNumber?: string;
  licenseNumber?: string;
  category?: DriverCategory;
  status?: DriverStatus;
  nationality?: string;
  weightKg?: number;
  parent?: CreateDriverParentInput;
  /** Optional second guardian for minors. */
  additionalParent?: CreateDriverParentInput;
  /**
   * When true, the driver account is managed directly by the team (no parent account required,
   * even for minors). The driver still counts towards the team's seat count.
   * Only TEAM_OWNER and COACH roles may set this flag.
   */
  teamManaged?: boolean;
}

export interface UpdateDriverRequest {
  name?: string;
  email?: string;
  username?: string;
  dateOfBirth?: string | null;
  driverNumber?: string | null;
  licenseNumber?: string | null;
  category?: DriverCategory;
}

export interface AccountSetupLink {
  userId: string;
  email: string;
  role: UserRole;
  registrationLink: string;
  expiresAt: string;
}

export interface CreateDriverResponse {
  driver: DriverListItem;
  accountSetupLinks: AccountSetupLink[];
}

export interface ListDriversQuery {
  status?: DriverStatus | 'ALL';
  search?: string;
  page?: number;
  perPage?: number;
  /** Skip parent and avatar lookups when a picker only needs the driver record itself. */
  includeRelations?: boolean;
}

export type MediaProcessingJobKind = 'GOPRO_INGEST';

export interface MediaProcessingSourceRef {
  bucket: string;
  objectPath: string;
  contentType?: string | null;
}

export interface MediaProcessingOutputSpec {
  /** Base path without extension. Example: teams/team-1/sessions/sess-1/media/GX010001 */
  basePath?: string;
  proxyHeight?: number;
  proxyBitrateKbps?: number;
  keepOriginal?: boolean;
}

export interface MediaProcessingJob {
  version: '1';
  kind: MediaProcessingJobKind;
  jobId: string;
  teamId: string;
  sessionId?: string | null;
  requestedByUserId?: string | null;
  requestedAt: string;
  source: MediaProcessingSourceRef;
  output?: MediaProcessingOutputSpec;
  metadata?: Record<string, string | number | boolean | null>;
}

export type MediaProcessingJobState = 'QUEUED' | 'PROCESSING' | 'COMPLETED' | 'FAILED';

export interface MediaProcessingJobOutputs {
  proxyObjectPath?: string;
  telemetrySummaryObjectPath?: string;
  telemetryObjectPath?: string;
  gpxObjectPath?: string;
}

export interface MediaProcessingJobRecord {
  id: string;
  kind: MediaProcessingJobKind;
  state: MediaProcessingJobState;
  projectId?: string;
  teamId: string;
  sessionId?: string | null;
  source: MediaProcessingSourceRef;
  output?: MediaProcessingOutputSpec;
  outputs?: MediaProcessingJobOutputs;
  error?: string;
  attempts: number;
  requestedAt: string;
  startedAt?: string;
  completedAt?: string;
  updatedAt: string;
}
